/**
 * Auth-gated tour completion.
 *
 * A signed-out visitor who has picked a tour date/time can choose to sign in
 * instead of typing their contact details. Their in-progress selection survives
 * the sign-in redirect (sessionStorage, same tab, cleared on read) and, on
 * return, the tour form restores it and — when the selection is complete —
 * submits automatically, so the auth-gated action *completes* on return
 * (USER_STORIES acceptance criterion), mirroring the favorite / save-search
 * intent flows.
 */
const KEY = 'goreal:tour-intent';

export interface TourIntent {
  listingId: string;
  date: string;
  time: string;
  message?: string;
}

export function setTourIntent(intent: TourIntent): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(intent));
  } catch {
    // storage unavailable (private mode) — sign-in still happens, the tour
    // selection just won't auto-restore; no crash.
  }
}

/** Read the pending tour intent WITHOUT clearing it. */
export function peekTourIntent(): TourIntent | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TourIntent>;
    if (!parsed || typeof parsed.listingId !== 'string') return null;
    return {
      listingId: parsed.listingId,
      date: typeof parsed.date === 'string' ? parsed.date : '',
      time: typeof parsed.time === 'string' ? parsed.time : '',
      message: typeof parsed.message === 'string' ? parsed.message : undefined,
    };
  } catch {
    return null;
  }
}

/** Clear any pending tour intent. */
export function clearTourIntent(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

/**
 * Read the pending tour intent and clear it (one-shot). Returns null when none.
 */
export function consumeTourIntent(): TourIntent | null {
  const intent = peekTourIntent();
  if (intent) clearTourIntent();
  return intent;
}

/**
 * Send a signed-out visitor to sign in, remembering the home + the date/time/
 * message they'd chosen, and the exact listing URL to return to — so the tour
 * finishes on the way back with nothing re-typed.
 */
export function startGuestTourSignIn(intent: TourIntent): void {
  setTourIntent(intent);
  const back = window.location.pathname + window.location.search;
  window.location.href = `/login?redirect=${encodeURIComponent(back)}`;
}
